import {
  Modal,
  TextField,
  useForm,
  PrimaryButton,
  SecondaryButton,
  Text,
} from '@weave/design-system';
import { useQueryNamespaceMutation } from 'apis/wetags/mutations';
import { useEffect, useState } from 'react';
import { theme } from '@weave/theme';

export const QueryNamespaceModal = (props: {
  isOpen: boolean;
  onClose: () => void;
  namespaceId: string;
}) => {
  const { isOpen, onClose, namespaceId } = props;
  const [result, setResult] = useState<boolean | null>(null);
  const {
    mutateAsync: queryNamespaceAsync,
    isLoading,
    error,
    reset: resetMutation,
  } = useQueryNamespaceMutation();

  const { formProps, getFieldProps, isComplete, reset } = useForm({
    fields: {
      query: {
        type: 'text',
        required: true,
        minLength: 1,
        maxLength: 1024,
        value: '',
      },
    },
    onSubmit: async (values) => {
      if (!values.query) return;
      const response = await queryNamespaceAsync({ namespaceId, query: values.query });
      setResult(response.result ?? false);
    },
  });

  //Cleanup on form submit and modal close
  useEffect(() => {
    if (!isOpen) {
      reset();
      resetMutation();
      setResult(null);
    }
  }, [isOpen]);

  return (
    <Modal show={isOpen} onClose={onClose} maxWidth={600}>
      <Modal.Header>Query {namespaceId}</Modal.Header>
      <Modal.Body>
        <p>
          <i>Search this namespace by entering a standard filter query.</i>
        </p>
        <p>
          Example:{' '}
          <code>
            entity_has(&quot;user123&quot;, &quot;new-ui&quot;) AND NOT
            entity_has(&quot;user123&quot;, &quot;beta-tester&quot;)
          </code>
        </p>
        <form {...formProps}>
          <TextField
            {...getFieldProps('query')}
            label={'Query'}
            placeholder={'entity_has("entity-id", "tag-id")'}
            disabled={isLoading}
          />
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <PrimaryButton type="submit" disabled={!isComplete || isLoading}>
              Run Query
            </PrimaryButton>
            <SecondaryButton type="button" onClick={onClose} disabled={isLoading}>
              Cancel
            </SecondaryButton>
          </div>
        </form>
        {error && (
          <Text color="error" style={{ marginTop: theme.spacing(2) }}>
            Failed to run query. Please check your query syntax and try again.
          </Text>
        )}
        {!error && result !== null && (
          <div
            style={{
              marginTop: theme.spacing(2),
              padding: theme.spacing(2),
              background: theme.colors.neutral10,
              borderRadius: theme.borderRadius.medium,
            }}
          >
            <Text weight="bold">{result ? 'Tag Exists ✅' : "Tag Doesn't Exist ❌"}</Text>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
